import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useApiClient } from '../hooks/useApiClient';

interface UserProfile {
  user_id: number;
  email: string;
  name: string;
  role: string;
  created_at: string;
  last_login: string | null;
  is_active: boolean;
  azure_ad_claims: {
    [key: string]: any;
  };
}

const UserInfo: React.FC = () => {
  const { user, isAuthenticated, getAccessToken } = useAuth();
  const { callApi } = useApiClient();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await callApi('/api/v1/user/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      setUserProfile(response);
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.message || 'Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  };

  const testUserProvisioning = async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await callApi('/api/v1/user/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      console.log('User provisioning test successful:', response);
      
      // Refresh the full profile after testing
      await fetchUserProfile();
    } catch (err: any) {
      console.error('Error testing user provisioning:', err);
      setError(err.message || 'Failed to test user provisioning');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserProfile();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Information
          </Typography>
          <Alert severity="info">
            Please log in to view user information.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          User Information & Provisioning Test
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            AuthGuard User Info:
          </Typography>
          <Typography variant="body2">
            <strong>Email:</strong> {user?.email || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Name:</strong> {user?.displayName || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Profile ID:</strong> {user?.profileId || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Resource:</strong> {user?.resourceId || 'N/A'}
          </Typography>
          {user?.isImpersonated && (
            <Chip label="Impersonated" color="warning" size="small" sx={{ mt: 1 }} />
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button 
            variant="contained" 
            onClick={testUserProvisioning}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            {loading ? <CircularProgress size={20} /> : 'Test User Provisioning'}
          </Button>
          <Button 
            variant="outlined" 
            onClick={fetchUserProfile}
            disabled={loading}
          >
            Refresh Profile
          </Button>
        </Box>

        {userProfile && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Database User Record:
            </Typography>
            <Box sx={{ ml: 2, mb: 2 }}>
              <Typography variant="body2">
                <strong>User ID:</strong> {userProfile.user_id}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {userProfile.email}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {userProfile.name}
              </Typography>
              <Typography variant="body2">
                <strong>Role:</strong> {userProfile.role}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {new Date(userProfile.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <strong>Last Login:</strong> {
                  userProfile.last_login 
                    ? new Date(userProfile.last_login).toLocaleString()
                    : 'Never'
                }
              </Typography>
              <Typography variant="body2">
                <strong>Active:</strong> {userProfile.is_active ? 'Yes' : 'No'}
              </Typography>
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Azure AD Information:
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2">
                <strong>Object ID:</strong> {userProfile.azure_ad_claims.oid || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Tenant ID:</strong> {userProfile.azure_ad_claims.tid || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Given Name:</strong> {userProfile.azure_ad_claims.given_name || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Family Name:</strong> {userProfile.azure_ad_claims.family_name || 'N/A'}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Azure AD Roles:</strong>
                </Typography>
                {userProfile.azure_ad_claims.roles?.map((role: string, index: number) => (
                  <Chip 
                    key={index} 
                    label={role} 
                    size="small" 
                    color="primary"
                    sx={{ mr: 0.5, mb: 0.5, ml: 1 }} 
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default UserInfo;